import {ReactElement, useEffect} from 'react';
import {
  SearchBox,
  Hits,
  useInstantSearch,
  Index,
  Configure,
  useSearchBox,
  // @ts-ignore:next-line
} from 'react-instantsearch';
import type {CollectionHit, ProductHit} from '~/types/algoliaTypes';
import {IconClose, IconSearch} from '../elements/Icon';
import {AlgoliaProductCard, InternalLink} from '..';
import {useRouteParams} from '@shopify/hydrogen';

/**
 * https://www.algolia.com/doc/guides/building-search-ui/what-is-instantsearch/react-hooks/
 */

// Debounce searches to regulate requests.
let debounceTimer: number;
const queryHook = (query: string, search: any) => {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    search(query);
  }, 300);
};

const productsIndexName =
  import.meta.env.PUBLIC_SANITY_DATASET === 'production'
    ? 'shopify_products'
    : 'dev_shopify_products';
const collectionsIndexName =
  import.meta.env.PUBLIC_SANITY_DATASET === 'production'
    ? 'shopify_collections'
    : 'dev_shopify_collections';

export function AlgoliaSearch({mobile = false}: {mobile?: boolean}) {
  const {query, clear} = useSearchBox();
  const routeParams = useRouteParams();

  // Prevent scrolling page when search is open
  useEffect(() => {
    if (query) {
      document.querySelector('body')?.classList.add('search-open');
    } else {
      document.querySelector('body')?.classList.remove('search-open');
    }
  }, [query]);

  // Close search when route changes
  useEffect(() => {
    clear();
  }, [routeParams]);

  const desktopSearchBoxClassNames = {
    root: 'flex items-center max-w-3xl w-full mx-auto',
    form: 'flex items-center w-full relative',
    input:
      'w-full pl-8 text-xs text-left rounded border-2 border-solid border-black',
    submit: 'absolute left-2',
    reset: 'absolute right-4',
  };

  const mobileSearchBoxClassNames = {
    root: 'bg-black pr-4 pl-4 pb-4',
    form: 'flex items-center w-full relative',
    input:
      'w-full pl-8 text-xs text-left rounded border-2 border-solid border-black',
    submit: 'absolute left-2',
    reset: 'absolute right-4',
  };

  return (
    <>
      <SearchBox
        queryHook={queryHook}
        placeholder="Hae tuotteita tai kategorioita"
        submitIconComponent={() => <IconSearch />}
        classNames={
          mobile ? mobileSearchBoxClassNames : desktopSearchBoxClassNames
        }
        translations={{
          submitButtonTitle: 'Hae',
          resetButtonTitle: 'Tyhjennä',
        }}
      />
      <EmptyQueryBoundary fallback={null}>
        <div className="absolute top-full left-0 right-0 z-50 bg-white h-drawer-height grid grid-cols-12 content-start overflow-y-auto p-4 lg:p-8">
          <div className="hidden lg:flex mb-4 col-span-12 justify-end">
            <button className="lg:flex items-center" onClick={clear}>
              <IconClose className="mr-2" />
              <span>Tyhjennä</span>
            </button>
          </div>
          <div className="col-span-12 mb-4 lg:col-span-3">
            <Index indexName={collectionsIndexName}>
              <Configure hitsPerPage={8} />
              <NoResultsBoundary fallback={<NoResults />}>
                <Hits
                  hitComponent={({hit}: {hit: CollectionHit}) => {
                    return (
                      <p
                        key={hit.objectID}
                        className="text-textTertiary mb-2 hover:underline"
                      >
                        <InternalLink
                          onClick={clear}
                          documentType="collection"
                          slug={hit.handle}
                        >
                          {hit.title}
                        </InternalLink>
                      </p>
                    );
                  }}
                />
              </NoResultsBoundary>
            </Index>
          </div>
          <div className="col-span-12 lg:col-span-9 pb-[100px]">
            <Index indexName={productsIndexName}>
              <Configure hitsPerPage={16} numericFilters={'price > 0'} />
              <NoResultsBoundary fallback={<NoResults />}>
                <Hits
                  classNames={{
                    list: 'grid-flow-row grid gap-2 gap-y-6 md:gap-4 lg:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
                  }}
                  hitComponent={({hit}: {hit: ProductHit}) => {
                    return <AlgoliaProductCard onClick={clear} product={hit} />;
                  }}
                />
              </NoResultsBoundary>
            </Index>
          </div>
        </div>
      </EmptyQueryBoundary>
    </>
  );
}

function EmptyQueryBoundary({
  children,
  fallback,
}: {
  children: ReactElement;
  fallback: ReactElement | null;
}) {
  const {indexUiState} = useInstantSearch();

  if (!indexUiState.query) {
    return fallback;
  }

  return children;
}

function NoResultsBoundary({
  children,
  fallback,
}: {
  children: ReactElement;
  fallback: ReactElement | null;
}) {
  const {results} = useInstantSearch();
  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned yet.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  const {indexUiState} = useInstantSearch();

  if (!indexUiState.query) {
    return null;
  }

  return (
    <div>
      <p>
        Ei tuloksia haulle <q>{indexUiState.query}</q>.
      </p>
    </div>
  );
}
